import React, { useState } from 'react';
import './App.css';
import ReactMarkdown from 'react-markdown';
import api from './api'; // Import the API configuration

function App() {
  const [activeTab, setActiveTab] = useState('analyze');

  return (
      <div className="App">
        <header className="App-header">
          <h1>REVIEW LENS</h1>
        </header>
        <div className="container">
          <div className="tabs">
            <button
                className={activeTab === 'scrape' ? 'active' : ''}
                onClick={() => setActiveTab('scrape')}
            >
              Scrape Reviews
            </button>
            <button
                className={activeTab === 'analyze' ? 'active' : ''}
                onClick={() => setActiveTab('analyze')}
            >
              Analyze Reviews
            </button>
          </div>

          <div className="tab-content">
            {activeTab === 'analyze' && <AnalyzeTab />}
            {activeTab === 'scrape' && <ScrapeTab setActiveTab={setActiveTab} />}
          </div>
        </div>
      </div>
  );
}

function AnalyzeTab() {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [filePath, setFilePath] = useState('');
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [activeResultTab, setActiveResultTab] = useState('summary');

  // Check if there's a file path in localStorage from ScrapeTab
  React.useEffect(() => {
    const savedFilePath = localStorage.getItem('lastFilePath');
    if (savedFilePath) {
      setFilePath(savedFilePath);
      setUploadSuccess(true);
      // Clear the localStorage after using it
      localStorage.removeItem('lastFilePath');
    }
  }, []);

  const handleFileChange = (e) => {
    if (e.target.files[0]) {
      setFile(e.target.files[0]);
      setError(null);
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();

    if (!file) {
      setError("Please select a CSV file first");
      return;
    }

    if (!file.name.endsWith('.csv')) {
      setError("Please upload a CSV file");
      return;
    }

    setUploading(true);
    setError(null);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await api.post('/upload-csv', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      setFilePath(response.data.file_path);
      setUploadSuccess(true);
    } catch (err) {
      console.error("Upload error:", err);
      setError(err.response?.data?.error || 'An error occurred during file upload');
    } finally {
      setUploading(false);
    }
  };

  const handleAnalyze = async () => {
    if (!filePath) {
      setError("Please upload a file first");
      return;
    }

    setAnalyzing(true);
    setError(null);

    try {
      const response = await api.post('/analyze', {
        file_path: filePath
      });

      setResult(response.data);
    } catch (err) {
      console.error("Analysis error:", err);
      setError(err.response?.data?.error || 'An error occurred while analyzing reviews');
    } finally {
      setAnalyzing(false);
    }
  };

  return (
      <div className="analyze-section">
        <h2>Analyze App Reviews</h2>

        <div className="upload-section">
          <h3>Step 1: Upload CSV File</h3>
          <form onSubmit={handleUpload}>
            <div className="form-group">
              <label htmlFor="csvFile">Select a CSV file with review data</label>
              <input
                  type="file"
                  id="csvFile"
                  accept=".csv"
                  onChange={handleFileChange}
                  disabled={uploading}
              />
            </div>
            <button
                type="submit"
                disabled={!file || uploading}
                className="primary-button"
            >
              {uploading ? 'Uploading...' : 'Upload File'}
            </button>
          </form>

          {uploadSuccess && (
              <div className="success-message">
                <span>✓</span> File uploaded successfully!
              </div>
          )}
        </div>

        <div className="analyze-action">
          <h3>Step 2: Start Analysis</h3>
          <p>After uploading your CSV file, click the button below to analyze the reviews with AI.</p>
          <button
              onClick={handleAnalyze}
              disabled={!uploadSuccess || analyzing}
              className="analyze-button"
          >
            {analyzing ? 'Analyzing ...' : 'Analyze Reviews with LLM'}
          </button>
        </div>

        {error && (
            <div className="error-message">
              {error}
            </div>
        )}

        {result && (
            <div className="results-container">
              <h3 className="results-title">Analysis Results</h3>

              <div className="result-tabs">
                <button
                    className={`result-tab ${activeResultTab === 'summary' ? 'active' : ''}`}
                    onClick={() => setActiveResultTab('summary')}
                >
                  Summary
                </button>
                <button
                    className={`result-tab ${activeResultTab === 'insights' ? 'active' : ''}`}
                    onClick={() => setActiveResultTab('insights')}
                >
                  Key Insights
                </button>
                <button
                    className={`result-tab ${activeResultTab === 'statistics' ? 'active' : ''}`}
                    onClick={() => setActiveResultTab('statistics')}
                >
                  Statistics
                </button>
              </div>

              <div className="result-content-wrapper">
                {activeResultTab === 'summary' && (
                    <div className="summary-container">
                      <h4 className="section-heading">Summary</h4>
                      <div className="markdown-content summary-content">
                        <ReactMarkdown
                            components={{
                              // Fixed the empty heading warnings by adding defaults
                              h2: ({node, children, ...props}) => <h2 className="summary-h2" {...props}>{children || 'Heading'}</h2>,
                              h3: ({node, children, ...props}) => <h3 className="summary-h3" {...props}>{children || 'Subheading'}</h3>,
                              h4: ({node, children, ...props}) => <h4 className="summary-h4" {...props}>{children || 'Section'}</h4>,
                              ul: ({node, ...props}) => <ul className="summary-list" {...props} />,
                              ol: ({node, ...props}) => <ol className="summary-list" {...props} />,
                              li: ({node, ...props}) => <li className="summary-list-item" {...props} />,
                              p: ({node, ...props}) => <p className="summary-paragraph" {...props} />,
                              strong: ({node, ...props}) => <strong className="summary-strong" {...props} />
                            }}
                        >
                          {result.summary}
                        </ReactMarkdown>
                      </div>
                    </div>
                )}

                {activeResultTab === 'insights' && (
                    <div className="insights-container">
                      <h4 className="section-heading">Key Insights</h4>
                      <div className="markdown-content insights-content">
                        <ReactMarkdown
                            components={{
                              // Fixed the empty heading warnings by adding defaults
                              h2: ({node, children, ...props}) => <h2 className="insights-h2" {...props}>{children || 'Heading'}</h2>,
                              h3: ({node, children, ...props}) => <h3 className="insights-h3" {...props}>{children || 'Subheading'}</h3>,
                              h4: ({node, children, ...props}) => <h4 className="insights-h4" {...props}>{children || 'Section'}</h4>,
                              ul: ({node, ...props}) => <ul className="insights-list" {...props} />,
                              ol: ({node, ...props}) => <ol className="insights-list" {...props} />,
                              li: ({node, ...props}) => <li className="insights-list-item" {...props} />,
                              p: ({node, ...props}) => <p className="insights-paragraph" {...props} />,
                              strong: ({node, ...props}) => <strong className="insights-strong" {...props} />
                            }}
                        >
                          {result.insights}
                        </ReactMarkdown>
                      </div>
                    </div>
                )}

                {activeResultTab === 'statistics' && (
                    <div className="statistics-container">
                      <h4 className="section-heading">Statistics</h4>

                      <div className="stats-overview">
                        <div className="stat-card">
                          <div className="stat-icon">📊</div>
                          <div className="stat-value">{result.stats.total_reviews}</div>
                          <div className="stat-label">Total Reviews</div>
                        </div>

                        <div className="stat-card">
                          <div className="stat-icon">⭐</div>
                          <div className="stat-value">{result.stats.average_rating.toFixed(2)}</div>
                          <div className="stat-label">Average Rating</div>
                        </div>
                      </div>

                      <div className="stats-details">
                        <div className="sentiment-section">
                          <h5 className="stats-subheading">Sentiment Distribution</h5>
                          {Object.entries(result.stats.sentiment_distribution || {}).map(([sentiment, count]) => (
                              <div key={sentiment} className="sentiment-item">
                                <div className="sentiment-header">
                                  <span className={`sentiment-badge ${sentiment.toLowerCase()}`}>
                                    {sentiment}
                                  </span>
                                  <span className="sentiment-count">
                                    {count} ({((count / result.stats.total_reviews) * 100).toFixed(1)}%)
                                  </span>
                                </div>
                                <div className="bar-container">
                                  <div
                                      className={`sentiment-bar ${sentiment.toLowerCase()}`}
                                      style={{width: `${(count / result.stats.total_reviews) * 100}%`}}
                                  ></div>
                                </div>
                              </div>
                          ))}
                        </div>

                        <div className="rating-section">
                          <h5 className="stats-subheading">Rating Distribution</h5>
                          {Object.entries(result.stats.rating_distribution || {}).sort((a, b) => b[0] - a[0]).map(([rating, count]) => (
                              <div key={rating} className="rating-item">
                                <div className="rating-header">
                                  <span className="rating-stars">
                                    {'★'.repeat(parseInt(rating))}{'☆'.repeat(5 - parseInt(rating))}
                                  </span>
                                  <span className="rating-count">
                                    {count} ({((count / result.stats.total_reviews) * 100).toFixed(1)}%)
                                  </span>
                                </div>
                                <div className="bar-container">
                                  <div
                                      className={`rating-bar rating-${rating}`}
                                      style={{width: `${(count / result.stats.total_reviews) * 100}%`}}
                                  ></div>
                                </div>
                              </div>
                          ))}
                        </div>
                      </div>
                    </div>
                )}

                <div className="result-actions">
                  <button
                      className="download-button"
                      onClick={() => window.open(`/download?file_path=${result.report_path}`)}
                  >
                    <span className="download-icon">📥</span> Download Full Report
                  </button>
                </div>
              </div>
            </div>
        )}

      </div>
  );
}

function ScrapeTab({ setActiveTab }) {
  const [store, setStore] = useState('');
  const [appName, setAppName] = useState('');
  const [appId, setAppId] = useState('');
  const [recordsCount, setRecordsCount] = useState(1000);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const response = await api.post('/scrape', {
        store,
        app_name: appName,
        app_id: appId,
        records_count: recordsCount
      });

      setResult(response.data);
    } catch (err) {
      console.error("Scrape error:", err);
      setError(err.response?.data?.error || 'An error occurred while scraping reviews');
    } finally {
      setLoading(false);
    }
  };

  return (
      <div className="scrape-section">
        <h2>Scrape App Reviews</h2>

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>App Store</label>
            <select
                value={store}
                onChange={(e) => setStore(e.target.value)}
                required
            >
              <option value="">Select App Store</option>
              <option value="apple">Apple App Store</option>
              <option value="google">Google Play Store</option>
            </select>
          </div>

          {store === 'apple' && (
              <div className="form-group">
                <label>App Name (For Apple App Store)</label>
                <input
                    type="text"
                    value={appName}
                    onChange={(e) => setAppName(e.target.value)}
                    required
                />
              </div>
          )}

          <div className="form-group">
            <label>App ID</label>
            <input
                type="text"
                value={appId}
                onChange={(e) => setAppId(e.target.value)}
                required
            />
            <small>
              {store === 'apple'
                  ? 'For Apple: Numeric ID (e.g., 310633997 for WhatsApp)'
                  : 'For Google: Package name (e.g., com.whatsapp)'}
            </small>
          </div>

          <div className="form-group">
            <label>Number of Reviews to Scrape</label>
            <input
                type="number"
                value={recordsCount}
                onChange={(e) => setRecordsCount(parseInt(e.target.value))}
                min="10"
                max="100000"
            />
          </div>

          <button
              type="submit"
              disabled={loading}
              className="primary-button"
          >
            {loading ? 'Scraping...' : 'Scrape Reviews'}
          </button>
        </form>

        {error && (
            <div className="error-message">
              {error}
            </div>
        )}

        {result && (
            <div className="scrape-results">
              <div className="success-message">
                <span>✓</span> {result.message}
              </div>

              <div className="results-grid">
                <div className="wordcloud">
                  <h3>Word Cloud</h3>
                  {result.wordcloud_base64 && (
                      <img
                          src={result.wordcloud_base64}
                          alt="Word Cloud"
                          className="wordcloud-image"
                      />
                  )}
                </div>

                <div className="actions">
                  <h3>Actions</h3>
                  <button
                      onClick={() => window.open(`/download?file_path=${result.file_path}`)}
                      className="action-button"
                  >
                    Download CSV
                  </button>

                  <button
                      onClick={() => {
                        // Save the file path to localStorage
                        localStorage.setItem('lastFilePath', result.file_path);
                        // Switch to analyze tab
                        setActiveTab('analyze');
                      }}
                      className="action-button"
                  >
                    Analyze These Reviews
                  </button>

                  <div className="file-path">
                    <label>File Path (for reference)</label>
                    <input
                        type="text"
                        value={result.file_path || ''}
                        readOnly
                        className="file-path-input"
                    />
                  </div>
                </div>
              </div>
            </div>
        )}
      </div>
  );
}

export default App;